import { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Loader2, Play, CheckCircle2, ListChecks, MessagesSquare,
  Plus, Trash2, Send, XCircle, Lock,
} from "lucide-react";
import { formatCurrencyBE } from "@/lib/currency";
import { hasRole } from "@/lib/roles";

const STATUS_META = {
  en_attente_rdv: { label: "En attente RDV", cls: "badge-neutral" },
  devise: { label: "Devisé", cls: "badge-warning" },
  a_planifier: { label: "À planifier", cls: "badge-warning" },
  en_cours: { label: "En cours", cls: "badge-info" },
  finalise: { label: "Finalisé", cls: "badge-success" },
  termine: { label: "Terminé", cls: "badge-neutral" },
};

export default function ChantierWorkflowSection({ chantier, quotes = [], onChanged, currentUser }) {
  const [steps, setSteps] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newStepTitle, setNewStepTitle] = useState("");
  const [newUpdateContent, setNewUpdateContent] = useState("");
  const [newUpdateType, setNewUpdateType] = useState("client");
  const [notifyClient, setNotifyClient] = useState(false);
  const [showForceClose, setShowForceClose] = useState(false);
  const [forceReason, setForceReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [showAcomptePreview, setShowAcomptePreview] = useState(false);
  const [acomptePreview, setAcomptePreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const signedQuote = quotes.find((q) => q.status === "signe");
  const isAdmin = hasRole(currentUser, ["admin"]);
  // Phase B rôle gestionnaire_chantier (2026-09-10) — force-close OUVERT au
  // gestionnaire_chantier, émission acompte INTERDITE.
  const isGestionnaireChantier = hasRole(currentUser, ["gestionnaire_chantier"]);
  const canForceCloseRole = isAdmin || isGestionnaireChantier;
  const canEmitInvoice = !isGestionnaireChantier;
  const chantierId = chantier?.id;

  const load = useCallback(async () => {
    if (!chantierId) return;
    setLoading(true);
    try {
      const [sRes, uRes] = await Promise.all([
        api.get(`/chantiers/${chantierId}/steps`),
        api.get(`/chantiers/${chantierId}/updates`),
      ]);
      setSteps(sRes.data);
      setUpdates(uRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [chantierId]);

  useEffect(() => { load(); }, [load]);

  const addStep = async () => {
    if (!newStepTitle.trim()) return;
    try {
      await api.post(`/chantiers/${chantierId}/steps`, { title: newStepTitle.trim() });
      setNewStepTitle("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteStep = async (id) => {
    try {
      await api.delete(`/chantiers/${chantierId}/steps/${id}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const completeStep = async (id) => {
    try {
      await api.post(`/chantiers/${chantierId}/steps/${id}/complete`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const startChantier = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/chantiers/${chantierId}/start`);
      toast.success("Chantier démarré", {
        description: data.email_status === "sent"
          ? "Email de planning envoyé au client"
          : data.email_error || "",
      });
      onChanged?.();
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const emitNextAcompte = async () => {
    if (!signedQuote) return;
    setPreviewLoading(true);
    setShowAcomptePreview(true);
    setAcomptePreview(null);
    try {
      const { data } = await api.get(`/quotes/${signedQuote.id}/next-acompte-preview`);
      setAcomptePreview(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setShowAcomptePreview(false);
    } finally { setPreviewLoading(false); }
  };

  const confirmEmitAcompte = async () => {
    if (!signedQuote || !acomptePreview) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/quotes/${signedQuote.id}/generate-invoice`, {
        type: "acompte", step_index: acomptePreview.step_index,
      });
      toast.success(`Facture ${data.invoice.number} émise`, {
        description: `${acomptePreview.step_label} — ${formatCurrencyBE(acomptePreview.amount_tvac)}`,
      });
      setShowAcomptePreview(false);
      setAcomptePreview(null);
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const downloadRecapPdf = async () => {
    try {
      const { data } = await api.get(`/chantiers/${chantierId}/recap/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `recap-chantier-${chantierId}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const postUpdate = async () => {
    if (!newUpdateContent.trim()) return;
    try {
      await api.post(`/chantiers/${chantierId}/updates`, {
        type: newUpdateType, content: newUpdateContent.trim(),
        notify_client: newUpdateType === "client" && notifyClient,
      });
      setNewUpdateContent("");
      setNotifyClient(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const forceClose = async () => {
    setBusy(true);
    try {
      await api.post(`/chantiers/${chantierId}/force-close`, { reason: forceReason.trim() });
      toast.success("Chantier clôturé");
      setShowForceClose(false);
      setForceReason("");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const status = chantier?.status;
  const meta = STATUS_META[status] || STATUS_META.en_attente_rdv;
  const canStart = status === "a_planifier" && steps.length > 0;
  const canEmitAcompte = canEmitInvoice && !!signedQuote && (status === "en_cours" || status === "a_planifier");
  const canForceClose = canForceCloseRole && status !== "termine";

  return (
    <Card className="border-slate-800 bg-slate-900/40" data-testid="chantier-workflow-section">
      <CardContent className="p-5">
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ListChecks className="h-4 w-4 text-slate-400" />
            <h3 className="text-sm font-semibold text-slate-200">Chantier</h3>
            <Badge className={meta.cls} data-testid="chantier-status-badge">{meta.label}</Badge>
          </div>
          <div className="flex gap-2">
            {canEmitAcompte && (
              <Button
                variant="outline" size="sm" onClick={emitNextAcompte} disabled={busy}
                className="border-slate-700 bg-slate-800/60 text-slate-200"
                data-testid="emit-next-acompte-btn"
              >
                {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <Send className="mr-1 h-3 w-3" />}
                Émettre prochain acompte
              </Button>
            )}
            {canStart && (
              <Button
                onClick={startChantier} disabled={busy}
                className="bg-[#5A6338] text-white hover:bg-[#495028]"
                data-testid="start-chantier-btn"
              >
                <Play className="mr-1 h-3 w-3" /> Valider et démarrer
              </Button>
            )}
            {canForceClose && (
              <Button
                variant="outline" size="sm" onClick={() => setShowForceClose(true)}
                className="border-[#8B2E1F]/50 bg-[#8B2E1F]/10 text-[#E9A98F] hover:bg-[#8B2E1F]/20"
                data-testid="force-close-btn"
              >
                <XCircle className="mr-1 h-3 w-3" /> Clôturer (admin)
              </Button>
            )}
          </div>
        </div>

        <Tabs defaultValue="steps">
          <TabsList className="bg-slate-800/50">
            <TabsTrigger value="steps" data-testid="tab-steps">Étapes ({steps.length})</TabsTrigger>
            <TabsTrigger value="feed" data-testid="tab-feed">Fil ({updates.length})</TabsTrigger>
            {status === "finalise" || status === "termine" ? (
              <TabsTrigger value="recap" data-testid="tab-recap">Récap</TabsTrigger>
            ) : null}
          </TabsList>

          {/* STEPS */}
          <TabsContent value="steps" className="mt-3">
            {loading ? <Loader2 className="mx-auto h-4 w-4 animate-spin text-slate-500" /> : (
              <>
                <ul className="space-y-2">
                  {steps.map((s, i) => (
                    <li key={s.id} className="flex items-start gap-3 rounded border border-slate-800 bg-slate-950/30 p-3"
                        data-testid={`step-item-${s.id}`}>
                      <div className={`mt-0.5 flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                        s.status === "done" ? "bg-[#7A8450] text-white" :
                        s.status === "current" ? "bg-[#D99A2B] text-white animate-pulse" :
                        "bg-slate-700 text-slate-400"
                      }`}>{i + 1}</div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-slate-100">{s.title}</div>
                        {s.description && <div className="text-xs text-slate-500">{s.description}</div>}
                        {s.status === "done" && s.completed_at && (
                          <div className="text-xs text-[#B8C784]">Terminée le {new Date(s.completed_at).toLocaleDateString("fr-BE")}</div>
                        )}
                      </div>
                      <div className="flex gap-1">
                        {s.status !== "done" && status === "en_cours" && (
                          <Button size="sm" onClick={() => completeStep(s.id)}
                                  className="bg-[#7A8450] text-white hover:bg-[#5A6338]"
                                  data-testid={`complete-step-${s.id}`}>
                            <CheckCircle2 className="h-3 w-3" />
                          </Button>
                        )}
                        {s.status !== "done" && (
                          <Button size="sm" variant="outline" onClick={() => deleteStep(s.id)}
                                  className="border-slate-700 bg-slate-800/60"
                                  data-testid={`delete-step-${s.id}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                        {s.status === "done" && <Lock className="mt-2 h-3 w-3 text-slate-600" />}
                      </div>
                    </li>
                  ))}
                </ul>
                {status !== "termine" && (
                  <div className="mt-3 flex gap-2">
                    <Input
                      placeholder="Titre de la nouvelle étape…"
                      value={newStepTitle}
                      onChange={(e) => setNewStepTitle(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && addStep()}
                      className="border-slate-700 bg-slate-950/40 text-slate-100"
                      data-testid="new-step-input"
                    />
                    <Button onClick={addStep} disabled={!newStepTitle.trim()}
                            className="bg-[#C4553B] text-white" data-testid="add-step-btn">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </>
            )}
          </TabsContent>

          {/* FEED */}
          <TabsContent value="feed" className="mt-3">
            {status !== "termine" && (
              <div className="mb-4 space-y-2 rounded border border-slate-800 bg-slate-950/30 p-3">
                <div className="flex gap-2">
                  <button
                    onClick={() => setNewUpdateType("client")}
                    className={`rounded px-3 py-1 text-xs ${newUpdateType === "client" ? "bg-[#C4553B] text-white" : "bg-slate-800 text-slate-400"}`}
                    data-testid="update-type-client">
                    Client (visible)
                  </button>
                  <button
                    onClick={() => setNewUpdateType("internal")}
                    className={`rounded px-3 py-1 text-xs ${newUpdateType === "internal" ? "bg-[#8B2E1F] text-white" : "bg-slate-800 text-slate-400"}`}
                    data-testid="update-type-internal">
                    Interne (privé)
                  </button>
                </div>
                <Textarea
                  placeholder={newUpdateType === "client" ? "Message visible par le client…" : "Note interne (jamais partagée)…"}
                  value={newUpdateContent}
                  onChange={(e) => setNewUpdateContent(e.target.value)}
                  className="border-slate-700 bg-slate-950/40 text-slate-100"
                  data-testid="new-update-textarea"
                />
                {newUpdateType === "client" && (
                  <label className="flex items-center gap-2 text-xs text-slate-400">
                    <input type="checkbox" checked={notifyClient}
                           onChange={(e) => setNotifyClient(e.target.checked)}
                           data-testid="notify-client-checkbox" />
                    Notifier le client par email
                  </label>
                )}
                <Button onClick={postUpdate} disabled={!newUpdateContent.trim()}
                        className="bg-[#C4553B] text-white" data-testid="post-update-btn">
                  <MessagesSquare className="mr-1 h-3 w-3" /> Publier
                </Button>
              </div>
            )}
            <ul className="space-y-2">
              {updates.map((u) => (
                <li key={u.id} className={`rounded border p-3 ${u.type === "internal" ? "border-[#8B2E1F]/40 bg-[#8B2E1F]/5" : "border-slate-800 bg-slate-950/30"}`}
                    data-testid={`update-item-${u.id}`}>
                  <div className="mb-1 flex items-center gap-2">
                    <Badge className={u.type === "internal" ? "bg-[#8B2E1F] text-white" : "bg-[#5A6338] text-white"}>
                      {u.type === "internal" ? "Interne" : "Client"}
                    </Badge>
                    <span className="text-xs text-slate-500">
                      {new Date(u.created_at).toLocaleString("fr-BE")}
                    </span>
                    {u.notified_client && <span className="text-xs text-[#B8C784]">· Email envoyé</span>}
                  </div>
                  <p className="whitespace-pre-wrap text-sm text-slate-200">{u.content}</p>
                </li>
              ))}
            </ul>
          </TabsContent>

          {/* RECAP */}
          {(status === "finalise" || status === "termine") && (
            <TabsContent value="recap" className="mt-3">
              <div className="rounded border border-slate-800 bg-slate-950/30 p-4">
                <h4 className="mb-2 font-semibold text-slate-100">Récap final</h4>
                {chantier.recap_signed_at ? (
                  <>
                    <p className="text-sm text-[#B8C784]">
                      ✓ Signé le {new Date(chantier.recap_signed_at).toLocaleDateString("fr-BE")} par {chantier.recap_signer_name}
                    </p>
                    <p className="mt-1 font-mono text-xs text-slate-500 break-all">
                      Hash SHA256 : {chantier.recap_signature_hash}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-slate-400">En attente de signature client</p>
                )}
                <button
                  onClick={downloadRecapPdf}
                  className="mt-3 inline-flex items-center gap-1 text-sm text-[#C4553B] hover:underline"
                  data-testid="recap-pdf-link"
                >
                  Télécharger le récap PDF
                </button>
                {chantier.force_closed_reason && (
                  <div className="mt-3 rounded border border-[#8B2E1F]/50 bg-[#8B2E1F]/10 p-2 text-xs text-[#E9A98F]">
                    <b>Clôture forcée</b> — motif : {chantier.force_closed_reason}
                  </div>
                )}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </CardContent>

      {/* Modal force-close */}
      <Dialog open={showForceClose} onOpenChange={setShowForceClose}>
        <DialogContent className="border-slate-800 bg-slate-900" data-testid="force-close-modal">
          <DialogHeader>
            <DialogTitle>Clôturer le chantier (admin)</DialogTitle>
            <DialogDescription className="text-slate-400">
              Action irréversible réservée aux administrateurs. Le motif sera consigné dans l&apos;audit trail.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label className="text-slate-300">Motif obligatoire (min. 5 caractères)</Label>
            <Textarea
              value={forceReason} onChange={(e) => setForceReason(e.target.value)}
              placeholder="Ex : Client injoignable après 3 relances écrites."
              className="border-slate-700 bg-slate-950/40 text-slate-100"
              data-testid="force-close-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowForceClose(false)}
                    className="border-slate-700 bg-slate-800/60">Annuler</Button>
            <Button onClick={forceClose} disabled={busy || forceReason.trim().length < 5}
                    className="bg-[#8B2E1F] text-white hover:bg-[#6A1F14]"
                    data-testid="force-close-confirm-btn">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirmer la clôture
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Modal preview émission acompte suivant */}
      <Dialog open={showAcomptePreview} onOpenChange={setShowAcomptePreview}>
        <DialogContent className="border-slate-800 bg-slate-900" data-testid="acompte-preview-modal">
          <DialogHeader>
            <DialogTitle>Émettre le prochain acompte</DialogTitle>
            <DialogDescription className="text-slate-400">
              Prévisualisation du montant avant émission. Confirmer déclenche numérotation FAC-YYYY-NNNN, PDF + email client.
            </DialogDescription>
          </DialogHeader>
          {previewLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            </div>
          ) : acomptePreview ? (
            <div className="space-y-3 text-sm">
              <div className="rounded border border-slate-800 bg-slate-950/40 p-3">
                <div className="text-xs uppercase tracking-wider text-slate-500">Étape {acomptePreview.step_index + 1}</div>
                <div className="mt-1 font-semibold text-slate-100">{acomptePreview.step_label}</div>
                <div className="text-xs text-slate-400">{acomptePreview.percent}% du total du devis</div>
              </div>
              <div className="grid grid-cols-3 gap-2 rounded border border-slate-800 bg-slate-950/40 p-3">
                <div>
                  <div className="text-xs text-slate-500">Montant HT</div>
                  <div className="tabular font-semibold text-slate-100">{formatCurrencyBE(acomptePreview.amount_ht)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">TVA</div>
                  <div className="tabular text-slate-200">{formatCurrencyBE(acomptePreview.amount_vat)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">TVAC</div>
                  <div className="tabular text-lg font-bold text-[#E9A98F]" data-testid="preview-amount-tvac">
                    {formatCurrencyBE(acomptePreview.amount_tvac)}
                  </div>
                </div>
              </div>
              {acomptePreview.remaining_steps?.length > 1 && (
                <div className="rounded border border-slate-800/60 bg-slate-950/20 p-2 text-xs text-slate-400">
                  <div className="mb-1 font-semibold text-slate-300">Échéances restantes :</div>
                  <ul className="space-y-0.5">
                    {acomptePreview.remaining_steps.map((s, i) => (
                      <li key={i} className={i === 0 ? "text-[#E9A98F]" : ""}>
                        {i === 0 ? "→ " : "  "}{s.label} ({s.percent}%)
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAcomptePreview(false)}
                    className="border-slate-700 bg-slate-800/60">Annuler</Button>
            <Button onClick={confirmEmitAcompte} disabled={busy || !acomptePreview}
                    className="bg-[#C4553B] text-white hover:bg-[#A8402C]"
                    data-testid="confirm-emit-acompte-btn">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirmer l&apos;émission
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
